// The radio's sound: the microphone into 40 ms Opus frames, and Opus frames out to your ear. The codec sits behind VoiceCodec
// so a WebAssembly Opus can drop in later (iPhone and iPad have no WebCodecs: every browser there is Safari's engine). Until
// that build is approved, a browser without WebCodecs gets the browser radio's honest message, and the rest of the radio
// (who's on, the channels, calls) still works.
//
// The microphone: the proximity voice's own capture is tapped when it's open (one getUserMedia for the whole game); with
// proximity voice off, the radio opens its own only while the key is held. Received audio plays "in your ear" (not placed in
// the room) through the playHead jitter scheduling of the browser radio, one timeline per transmission. A channel can be
// routed somewhere else instead (the intercom's speakers, later): see radioSpeakerRoute in client/ambience.ts.
import { RADIO_BITRATE, RADIO_FRAME_MS } from "../shared/radio.js";
import { radioSpeakerRoute } from "./ambience.js";
import type { RadioAudioPort } from "./radio.js";
import { registerAudio } from "./voice.js";

const RATE = 48000, FRAME = (RATE * RADIO_FRAME_MS) / 1000;   // 1920 samples a frame

// ---------- the codec ----------
export interface OpusEncoderPort { encode(pcm: Float32Array): void; flush(): Promise<void>; close(): void }
export interface OpusDecoderPort { decode(opus: Uint8Array): void; close(): void }
export interface VoiceCodec {
  readonly name: string;
  supported(): boolean;
  encoder(onFrame: (opus: Uint8Array) => void, onError: (e: string) => void): OpusEncoderPort;          // 48 kHz mono in, 40 ms Opus frames out
  decoder(onPcm: (pcm: Float32Array, rate: number) => void, onError: (e: string) => void): OpusDecoderPort;
}
// the browser's own Opus (Chrome, Edge and most Chromium browsers; Firefox recent; not Safari / iOS)
export const webCodecsOpus: VoiceCodec = {
  name: "WebCodecs",
  supported: () => typeof AudioEncoder !== "undefined" && typeof AudioDecoder !== "undefined",
  encoder(onFrame, onError) {
    let n = 0;
    const enc = new AudioEncoder({ output: (chunk) => { const u = new Uint8Array(chunk.byteLength); chunk.copyTo(u); onFrame(u); }, error: (e) => onError("Encoder: " + e.message) });
    enc.configure({ codec: "opus", sampleRate: RATE, numberOfChannels: 1, bitrate: RADIO_BITRATE, opus: { frameDuration: RADIO_FRAME_MS * 1000 } } as AudioEncoderConfig);
    return {
      encode(pcm) {
        if (enc.state !== "configured") return;
        const ad = new AudioData({ format: "f32", sampleRate: RATE, numberOfFrames: pcm.length, numberOfChannels: 1, timestamp: n++ * RADIO_FRAME_MS * 1000, data: pcm as BufferSource });
        try { enc.encode(ad); } catch { /* closed */ } ad.close();
      },
      async flush() { try { if (enc.state === "configured") await enc.flush(); } catch { /* closed */ } },
      close() { try { enc.close(); } catch { /* closed */ } },
    };
  },
  decoder(onPcm, onError) {
    let n = 0;
    const dec = new AudioDecoder({
      output: (ad) => { try { const f = new Float32Array(ad.numberOfFrames); ad.copyTo(f, { planeIndex: 0, format: "f32" }); onPcm(f, ad.sampleRate); } catch { /* bad frame */ } try { ad.close(); } catch { /* closed */ } },
      error: (e) => onError("Decoder: " + e.message),
    });
    dec.configure({ codec: "opus", sampleRate: RATE, numberOfChannels: 1 });
    return {
      decode(opus) { if (dec.state !== "configured") return; try { dec.decode(new EncodedAudioChunk({ type: "key", timestamp: n++ * RADIO_FRAME_MS * 1000, data: opus })); } catch { /* bad frame */ } },
      close() { try { dec.close(); } catch { /* closed */ } },
    };
  },
};
// every browser on iPhone / iPad runs Apple's Safari engine (Apple requires it), so "use Chrome" is no help there
export const IS_IOS = typeof navigator !== "undefined" && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));
export function codecMissingText(): string {
  return IS_IOS
    ? "Voice isn't available on iPhone/iPad yet: every browser on iOS runs Safari's engine, and it doesn't include the voice codec the radio uses. You can see who's on and switch channels, but this device can't talk or listen until the radio carries its own codec."
    : "This browser doesn't include the voice codec the radio uses. Use a current Chrome or Edge on a computer or Android phone.";
}

const WORKLET_SRC = `
  class Cap extends AudioWorkletProcessor {
    constructor(o){ super(); this.n=o.processorOptions.frameSamples; this.buf=new Float32Array(this.n); this.i=0; }
    process(inputs){ const ch=inputs[0][0]; if(!ch) return true;
      for(let k=0;k<ch.length;k++){ this.buf[this.i++]=ch[k];
        if(this.i>=this.n){ this.port.postMessage(this.buf.slice(0)); this.i=0; } } return true; } }
  registerProcessor('radiocap', Cap);`;

export interface RadioAudioOpts {
  codec: VoiceCodec;
  mic: () => MediaStream | null;          // the proximity voice's open microphone, if it has one (tapped, not opened twice)
  micId: () => string;                    // the chosen microphone, when the radio opens its own
  onError: (text: string) => void;
}
interface Rx { dec: OpusDecoderPort; playHead: number; out: AudioNode; rec: { decoded(): void; played(): void } }

// the walkie's onboard effects on INCOMING audio (walkie.js): 15-band peaking EQ -> compressor -> dry + tanh drive -> out.
// Presets as the desktop radio has them (Grey, 2026-10-17: "are the effects in?")
export const EQ_FREQS = [25, 40, 63, 100, 160, 250, 400, 630, 1000, 1600, 2500, 4000, 6300, 10000, 16000]; const EQ_Q = 1.4;
export type RadioFx = "clean" | "radio" | "telephone" | "megaphone";
export interface FxSetting { comp: number; dist: number; eq: number[] }
export const FX_PRESETS: Record<RadioFx, { label: string } & FxSetting> = {
  clean: { label: "Clean", comp: 0, dist: 0, eq: EQ_FREQS.map(() => 0) },
  radio: { label: "Radio", comp: 30, dist: 20, eq: [-12, -12, -12, -12, -9, -5, 1, 2, 2, 4, 2, -9, -12, -12, -12] },
  telephone: { label: "Telephone", comp: 20, dist: 8, eq: [-12, -12, -12, -12, -12, -10, -6, 2, 3, 2, 0, -10, -12, -12, -12] },
  megaphone: { label: "Megaphone", comp: 45, dist: 55, eq: [-12, -12, -12, -10, -6, 0, 5, 6, 6, 5, 2, -4, -9, -12, -12] },
};
// the walkie's own numbers, and the admin's (Admin > Radio) laid over them for everyone aboard; Clean stays clean
export const FX_BUILTIN: Record<RadioFx, FxSetting> = Object.fromEntries(Object.entries(FX_PRESETS).map(([k, v]) => [k, { comp: v.comp, dist: v.dist, eq: [...v.eq] }])) as Record<RadioFx, FxSetting>;
const fxListeners = new Set<() => void>();
export function setFxOverrides(o: Record<string, FxSetting>) {
  for (const k of ["radio", "telephone", "megaphone"] as RadioFx[]) {
    const v = o[k], b = FX_BUILTIN[k], ok = !!v && Number.isFinite(v.comp) && Number.isFinite(v.dist) && Array.isArray(v.eq) && v.eq.length === EQ_FREQS.length;
    const src = ok ? v : b; FX_PRESETS[k].comp = src.comp; FX_PRESETS[k].dist = src.dist; FX_PRESETS[k].eq = [...src.eq];
  }
  for (const f of fxListeners) f();
}
export const fxIsBuiltin = (k: RadioFx) => { const p = FX_PRESETS[k], b = FX_BUILTIN[k]; return p.comp === b.comp && p.dist === b.dist && p.eq.every((x, i) => x === b.eq[i]); };
// 15-band peaking EQ -> compressor -> dry + tanh drive -> out
export function fxChain(ac: BaseAudioContext) {
  const input = ac.createGain(); let prev: AudioNode = input;
  const bands = EQ_FREQS.map((f) => { const b = ac.createBiquadFilter(); b.type = "peaking"; b.frequency.value = f; b.Q.value = EQ_Q; prev.connect(b); prev = b; return b; });
  const comp = ac.createDynamicsCompressor(), dry = ac.createGain(), wet = ac.createGain(), shaper = ac.createWaveShaper(), out = ac.createGain();
  try { shaper.oversample = "2x"; } catch { /* fine */ }
  prev.connect(comp); comp.connect(dry); comp.connect(shaper); shaper.connect(wet); dry.connect(out); wet.connect(out);
  const apply = (p: FxSetting) => {
    const a = p.comp / 100, d = p.dist / 100;
    bands.forEach((b, i) => (b.gain.value = p.eq[i] ?? 0));
    comp.threshold.value = -50 * a; comp.knee.value = 30; comp.ratio.value = 1 + 11 * a; comp.attack.value = 0.003; comp.release.value = 0.25;
    wet.gain.value = d; dry.gain.value = 1 - d; shaper.curve = driveCurve(p.dist); out.gain.value = 1;
  };
  return { input, out, apply };
}
// the admin's ▶ Test: a couple of seconds of a voice-like buzz (a low sawtooth, its syllables rising and falling) through the effect
let testCtx: AudioContext | null = null;
export function fxTest(p: FxSetting) {
  try {
    testCtx ??= new AudioContext(); const ac = testCtx; void ac.resume(); const t0 = ac.currentTime + 0.05, len = 2.4;
    const ch = fxChain(ac); ch.apply(p); ch.out.connect(ac.destination);
    const o1 = ac.createOscillator(), o2 = ac.createOscillator(), env = ac.createGain(), form = ac.createBiquadFilter();
    o1.type = "sawtooth"; o2.type = "square"; o1.frequency.setValueAtTime(130, t0); o2.frequency.setValueAtTime(262, t0);
    for (let i = 0; i < 8; i++) { const at = t0 + i * 0.3; o1.frequency.linearRampToValueAtTime(120 + ((i * 37) % 50), at + 0.15); form.frequency.setValueAtTime(600 + ((i * 410) % 1800), at); }
    form.type = "bandpass"; form.Q.value = 1.2; env.gain.setValueAtTime(0, t0);
    for (let i = 0; i < 8; i++) { const at = t0 + i * 0.3; env.gain.linearRampToValueAtTime(0.5, at + 0.05); env.gain.linearRampToValueAtTime(0.08, at + 0.26); }
    env.gain.linearRampToValueAtTime(0, t0 + len);
    const mix = ac.createGain(); mix.gain.value = 0.6; o1.connect(mix); o2.connect(mix); mix.connect(form); form.connect(env); mix.connect(env); env.connect(ch.input);
    o1.start(t0); o2.start(t0); o1.stop(t0 + len); o2.stop(t0 + len);
    o1.onended = () => { try { ch.out.disconnect(); } catch { /* done */ } };
  } catch { /* no audio here */ }
}
function driveCurve(amount: number): Float32Array<ArrayBuffer> { const n = 1024, c = new Float32Array(n), drive = 1 + (Math.max(0, Math.min(100, amount)) / 100) * 40, norm = Math.tanh(drive) || 1; for (let i = 0; i < n; i++) { const x = (i / (n - 1)) * 2 - 1; c[i] = Math.tanh(drive * x) / norm; } return c; }
export class RadioAudio implements RadioAudioPort {
  volume = 1; fx: RadioFx = "radio";
  private chain: ReturnType<typeof fxChain> | null = null;
  private txCtx: AudioContext | null = null; private worklet: AudioWorkletNode | null = null; private src: MediaStreamAudioSourceNode | null = null;
  private own: MediaStream | null = null; private enc: OpusEncoderPort | null = null; private capturing = false;
  private rxCtx: AudioContext | null = null; private ear: GainNode | null = null; private rx = new Map<string, Rx>();
  constructor(private o: RadioAudioOpts) { fxListeners.add(() => { if (this.rxCtx) this.wireFx(); }); }   // the admin changed the sound: heard at once
  get canTalk() { return this.o.codec.supported() && !!navigator.mediaDevices?.getUserMedia; }
  get canHear() { return this.o.codec.supported(); }
  get why() { return !this.o.codec.supported() ? codecMissingText() : !navigator.mediaDevices?.getUserMedia ? "The microphone needs a secure (https) page." : ""; }
  setVolume(v: number) { this.volume = v; if (this.ear && this.rxCtx) this.ear.gain.setTargetAtTime(v, this.rxCtx.currentTime, 0.03); }
  // the effects: built once on the receive side; Clean bypasses them (the ear straight to the speakers)
  setFx(fx: RadioFx) { this.fx = fx; if (this.rxCtx) this.wireFx(); }
  private wireFx() {
    const ac = this.rxCtx!, ear = this.ear!; try { ear.disconnect(); } catch { /* not wired yet */ }
    if (this.fx === "clean") { ear.connect(ac.destination); return; }
    if (!this.chain) { this.chain = fxChain(ac); this.chain.out.connect(ac.destination); }
    this.chain.apply(FX_PRESETS[this.fx]);
    ear.connect(this.chain.input);
  }

  // ---------- transmit ----------
  async startTx(onOpus: (opus: Uint8Array) => void): Promise<boolean> {
    if (!this.canTalk) { this.o.onError(this.why); return false; }
    try {
      if (!this.txCtx) {
        const ac = new AudioContext({ sampleRate: RATE }); registerAudio(ac); this.txCtx = ac;
        const url = URL.createObjectURL(new Blob([WORKLET_SRC], { type: "application/javascript" }));
        await ac.audioWorklet.addModule(url); URL.revokeObjectURL(url);
        this.worklet = new AudioWorkletNode(ac, "radiocap", { processorOptions: { frameSamples: FRAME } });
        const sink = ac.createGain(); sink.gain.value = 0; this.worklet.connect(sink).connect(ac.destination);   // the worklet only runs when it reaches an output
        this.worklet.port.onmessage = (ev: MessageEvent<Float32Array>) => { if (this.capturing) this.enc?.encode(ev.data); };
      }
      const ac = this.txCtx; if (ac.state !== "running") await ac.resume().catch(() => {});
      let stream = this.o.mic();                                                    // the proximity voice's mic, when it's open
      if (!stream || !stream.getAudioTracks().some((t) => t.readyState === "live")) {
        const id = this.o.micId();
        this.own = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: id ? { exact: id } : undefined, echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 }, video: false })
          .catch(() => navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false }));
        stream = this.own;
      }
      this.enc = this.o.codec.encoder(onOpus, (e) => this.o.onError(e));
      this.src = ac.createMediaStreamSource(stream); this.src.connect(this.worklet!);
      this.capturing = true; return true;
    } catch (e) { this.o.onError("Microphone unavailable: " + ((e as Error)?.message ?? e)); this.closeCapture(); return false; }
  }
  async stopTx(): Promise<void> {
    this.capturing = false;
    const enc = this.enc; this.enc = null;
    if (enc) { await enc.flush(); enc.close(); }
    this.closeCapture();
  }
  private closeCapture() {
    try { this.src?.disconnect(); } catch { /* gone */ } this.src = null;
    if (this.own) { for (const t of this.own.getTracks()) t.stop(); this.own = null; }   // the radio's own mic: only while keyed
  }

  // ---------- receive ----------
  private ensureRx(): AudioContext {
    if (!this.rxCtx) { const ac = new AudioContext({ sampleRate: RATE }); registerAudio(ac); this.rxCtx = ac; this.ear = ac.createGain(); this.ear.gain.value = this.volume; this.wireFx(); }
    return this.rxCtx;
  }
  play(stream: string, opus: Uint8Array, canon: string, rec: { decoded(): void; played(): void }) {
    if (!this.canHear) return;
    const ac = this.ensureRx(); if (ac.state !== "running") void ac.resume().catch(() => {});
    let r = this.rx.get(stream);
    if (!r) {
      const out = radioSpeakerRoute(canon)?.(ac) ?? this.ear!;                     // in your ear, unless the channel is routed to a speaker
      const rr: Rx = { dec: null as unknown as OpusDecoderPort, playHead: 0, out, rec };
      rr.dec = this.o.codec.decoder((pcm, rate) => { rr.rec.decoded(); this.schedule(rr, pcm, rate); }, (e) => this.o.onError(e));
      this.rx.set(stream, rr); r = rr;
    }
    r.rec = rec; r.dec.decode(opus);
  }
  // each decoded 40 ms frame plays at the stream's playHead: a little ahead of now, then back to back (the jitter buffer)
  private schedule(r: Rx, pcm: Float32Array, rate: number) {
    const ac = this.rxCtx; if (!ac) return;
    try {
      const buf = ac.createBuffer(1, pcm.length, rate); buf.copyToChannel(pcm as Float32Array<ArrayBuffer>, 0);
      const node = ac.createBufferSource(); node.buffer = buf; node.connect(r.out);
      const now = ac.currentTime; if (r.playHead < now + 0.02) r.playHead = now + 0.02;
      node.start(r.playHead); r.playHead += buf.duration; r.rec.played();
    } catch { /* a bad frame */ }
  }
  endStream(stream: string) { const r = this.rx.get(stream); if (!r) return; this.rx.delete(stream); setTimeout(() => r.dec.close(), 500); }
}
